import { Component, OnInit, Input, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { from } from 'rxjs';
import { mergeMap, toArray } from 'rxjs/operators';
import { ResourceDistributionService, EmployeeActiveDay } from './resource-distribution.service';

type CellState = 'normal' | 'add' | 'added' | 'pre-delete';

interface Employee {
  u_id: number;
  full_name: string;
  hasSchedule: boolean;
}

interface District {
  district_id: number;
  district_name: string;
}

@Component({
  selector: 'app-resource-distribution',
  templateUrl: './resource-distribution.component.html',
  styleUrls: ['./resource-distribution.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ResourceDistributionComponent implements OnInit {
  @Input() filter: any;
  @Input() calendar: any;

  @ViewChild('headerScroll') headerScroll: ElementRef;
  @ViewChild('bodyScroll') bodyScroll: ElementRef;

  isLoading = false;
  isHeaderScrolling = false;
  isBodyScrolling = false;

  districts: District[] = [];
  districtsMap: Map<number, District> = new Map();
  usersMap: Map<number, any> = new Map();
  selectedDistricts: District[] = [];
  filteredDistricts: District[] = [];
  employees: Employee[] = [];
  filteredEmployees: Employee[] = [];
  employeeSearchText = '';
  employeeActiveDays: EmployeeActiveDay[] = [];
  cellStates: Map<string, CellState> = new Map();
  originalCellStates: Map<string, boolean> = new Map();
  cellIdMap: Map<string, number> = new Map();
  showActionButtons = false;
  isSaving = false;

  showNotification = false;
  notificationText = '';

  openedPopupEmployeeId: number | null = null;

  days = [1, 2, 3, 4, 5, 6, 7];

  private readonly saveConcurrency = 4;

  constructor(
    private resourceDistributionService: ResourceDistributionService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.isLoading = true;

    const params: any = { limit: 10000 };

    if (this.filter && this.filter.plotSelected && this.filter.plotSelected.sector_id) {
      params.sector_id = this.filter.plotSelected.sector_id;
    }

    this.resourceDistributionService.getEmployeeActiveDays(params).subscribe(
      (response) => {
        const activeDaysData = response.data.results || response.data || [];
        this.employeeActiveDays = activeDaysData;

        const usersMap = new Map<number, { userId: number; fullName: string }>();
        activeDaysData.forEach((day: EmployeeActiveDay) => {
          if (day.user && !usersMap.has(day.user.user_id)) {
            const fullName = [day.user.last_name, day.user.first_name, day.user.patronymic_name].filter(Boolean).join(' ');
            usersMap.set(day.user.user_id, { userId: day.user.user_id, fullName });
          }
        });

        this.employees = Array.from(usersMap.values()).map((u) => ({
          u_id: u.userId,
          full_name: u.fullName,
          hasSchedule: true,
        }));

        const districtsMap = new Map<number, { districtId: number; districtName: string }>();
        activeDaysData.forEach((day: EmployeeActiveDay) => {
          if (day.district && !districtsMap.has(day.district.d_id)) {
            districtsMap.set(day.district.d_id, {
              districtId: day.district.d_id,
              districtName: day.district.district_name,
            });
          }
        });

        this.districts = Array.from(districtsMap.values()).map((d) => ({
          district_id: d.districtId,
          district_name: d.districtName,
        }));
        this.filteredDistricts = this.districts.slice();
        this.filteredEmployees = this.employees.slice();

        this.initCellStatesFromData(activeDaysData);

        this.isLoading = false;
        this.cdr.markForCheck();
      },
      (error) => {
        console.error('Error loading data:', error);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    );
  }

  initCellStatesFromData(activeDays: EmployeeActiveDay[]) {
    this.cellStates.clear();
    this.originalCellStates.clear();
    this.cellIdMap.clear();

    activeDays.forEach((day) => {
      if (!day.user || !day.district) return;
      const userId = day.user.user_id;
      const districtId = day.district.d_id;
      if (!userId || !districtId) return;

      const key = this.getCellKey(userId, districtId, day.week_day);
      this.cellIdMap.set(key, day.id);
      this.originalCellStates.set(key, day.is_active);

      if (day.is_active) {
        this.cellStates.set(key, 'added');
      } else {
        this.cellStates.set(key, 'normal');
      }
    });
  }

  onChangeDistricts(selected: District[]) {
    this.selectedDistricts = selected;
  }

  applyDistrictFilter() {
    if (this.selectedDistricts && this.selectedDistricts.length > 0) {
      this.filteredDistricts = this.districts.filter((d) => this.selectedDistricts.some((s) => s.district_id === d.district_id));
    } else {
      this.filteredDistricts = this.districts.slice();
    }
    this.cdr.markForCheck();
  }

  resetDistrictFilter() {
    this.selectedDistricts = [];
    this.filteredDistricts = this.districts.slice();
    this.cdr.markForCheck();
  }

  applyFilter() {
    if (this.selectedDistricts && this.selectedDistricts.length > 0) {
      this.filteredDistricts = this.districts.filter((d) => this.selectedDistricts.some((s) => s.district_id === d.district_id));
    } else {
      this.filteredDistricts = this.districts.slice();
    }

    if (this.employeeSearchText && this.employeeSearchText.trim()) {
      const searchLower = this.employeeSearchText.toLowerCase().trim();
      this.filteredEmployees = this.employees.filter((e) => e.full_name.toLowerCase().includes(searchLower));
    } else {
      this.filteredEmployees = this.employees.slice();
    }
    this.cdr.markForCheck();
  }

  resetFilter() {
    this.selectedDistricts = [];
    this.employeeSearchText = '';
    this.filteredDistricts = this.districts.slice();
    this.filteredEmployees = this.employees.slice();
    this.cdr.markForCheck();
  }

  getCellKey(userId: number, districtId: number, weekDay: number): string {
    return `${userId}-${districtId}-${weekDay}`;
  }

  getCellState(userId: number, districtId: number, weekDay: number): CellState {
    const key = this.getCellKey(userId, districtId, weekDay);
    return this.cellStates.get(key) || 'normal';
  }

  hasCellRecord(userId: number, districtId: number, weekDay: number): boolean {
    return this.cellIdMap.has(this.getCellKey(userId, districtId, weekDay));
  }

  getEmployeeCellKeys(userId: number): string[] {
    const keys: string[] = [];

    this.filteredDistricts.forEach((district) => {
      this.days.forEach((day) => {
        const key = this.getCellKey(userId, district.district_id, day);

        if (this.cellIdMap.has(key)) {
          keys.push(key);
        }
      });
    });

    return keys;
  }

  getDistrictCellKeys(districtId: number): string[] {
    const keys: string[] = [];

    this.filteredEmployees.forEach((employee) => {
      if (!employee.hasSchedule) {
        return;
      }

      this.days.forEach((day) => {
        const key = this.getCellKey(employee.u_id, districtId, day);

        if (this.cellIdMap.has(key)) {
          keys.push(key);
        }
      });
    });

    return keys;
  }

  fillEmployeeCells(userId: number, event: Event) {
    event.stopPropagation();
    this.applyBulkState(this.getEmployeeCellKeys(userId), true, this.filteredDistricts.length * this.days.length);
  }

  clearEmployeeCells(userId: number, event: Event) {
    event.stopPropagation();
    this.applyBulkState(this.getEmployeeCellKeys(userId), false, this.filteredDistricts.length * this.days.length);
  }

  fillDistrictCells(districtId: number, event: Event) {
    event.stopPropagation();
    this.applyBulkState(this.getDistrictCellKeys(districtId), true, this.filteredEmployees.length * this.days.length);
  }

  clearDistrictCells(districtId: number, event: Event) {
    event.stopPropagation();
    this.applyBulkState(this.getDistrictCellKeys(districtId), false, this.filteredEmployees.length * this.days.length);
  }

  // Массовое заполнение и очистка: меняем только состояние ячеек, запись уходит по кнопке «Сохранить».
  applyBulkState(keys: string[], isActive: boolean, totalCells: number) {
    if (!keys.length) {
      this.notify('Нет ячеек для изменения: по этим сотрудникам и районам нет записей в графике.');
      return;
    }

    let changed = 0;

    keys.forEach((key) => {
      const wasActive = this.originalCellStates.get(key);
      let nextState: CellState;

      if (isActive) {
        nextState = wasActive ? 'added' : 'add';
      } else {
        nextState = wasActive ? 'pre-delete' : 'normal';
      }

      if (this.cellStates.get(key) !== nextState) {
        changed++;
      }

      this.cellStates.set(key, nextState);
    });

    this.updateActionButtonsVisibility();
    this.cdr.markForCheck();

    const skipped = totalCells - keys.length;

    if (!changed) {
      this.notify('Все ячейки уже в нужном состоянии.');
    } else if (skipped > 0) {
      this.notify('Ячеек изменено: ' + changed + '. Пропущено без записи в графике: ' + skipped + '.');
    }
  }

  onCellClick(userId: number, districtId: number, weekDay: number, hasSchedule: boolean) {
    if (!hasSchedule) {
      return;
    }

    if (!this.hasCellRecord(userId, districtId, weekDay)) {
      return;
    }

    const key = this.getCellKey(userId, districtId, weekDay);
    const currentState = this.getCellState(userId, districtId, weekDay);

    if (currentState === 'normal') {
      this.cellStates.set(key, 'add');
    } else if (currentState === 'add') {
      this.cellStates.set(key, 'normal');
    } else if (currentState === 'added') {
      this.cellStates.set(key, 'pre-delete');
    } else if (currentState === 'pre-delete') {
      this.cellStates.set(key, 'added');
    }

    this.updateActionButtonsVisibility();
    this.cdr.markForCheck();
  }

  updateActionButtonsVisibility() {
    let hasChanges = false;
    this.cellStates.forEach((state) => {
      if (state === 'add' || state === 'pre-delete') {
        hasChanges = true;
      }
    });
    this.showActionButtons = hasChanges;
  }

  saveChanges() {
    const updateRequests: { id: number; isActive: boolean; key: string }[] = [];

    this.cellStates.forEach((state, key) => {
      if (state === 'add' || state === 'pre-delete') {
        const recordId = this.cellIdMap.get(key);

        if (!recordId) {
          return;
        }

        if (state === 'add') {
          updateRequests.push({ id: recordId, isActive: true, key });
        } else if (state === 'pre-delete') {
          updateRequests.push({ id: recordId, isActive: false, key });
        }
      }
    });

    if (updateRequests.length === 0) {
      this.showActionButtons = false;
      return;
    }

    this.isSaving = true;
    this.cdr.markForCheck();

    const requests = updateRequests.map((req) => this.resourceDistributionService.updateEmployeeActiveDay(req.id, req.isActive));

    from(requests)
      .pipe(
        mergeMap((request) => request, this.saveConcurrency),
        toArray()
      )
      .subscribe(
        () => {
          let hasAdditions = false;
          let hasDeletions = false;

          updateRequests.forEach((req) => {
            if (req.isActive) {
              this.cellStates.set(req.key, 'added');
              this.originalCellStates.set(req.key, true);
              hasAdditions = true;
            } else {
              this.cellStates.set(req.key, 'normal');
              this.originalCellStates.set(req.key, false);
              hasDeletions = true;
            }
          });

          this.showActionButtons = false;
          this.isSaving = false;

          if (hasAdditions && hasDeletions) {
            this.notify('Изменения успешно сохранены');
          } else if (hasAdditions) {
            this.notify('Районы успешно добавлены');
          } else if (hasDeletions) {
            this.notify('Районы успешно удалены');
          } else {
            this.cdr.markForCheck();
          }
        },
        (error) => {
          console.error('Ошибка при сохранении изменений:', error);
          this.isSaving = false;
          this.notify('Ошибка при сохранении изменений');
        }
      );
  }

  notify(text: string) {
    this.notificationText = text;
    this.showNotification = true;
    this.cdr.markForCheck();

    setTimeout(() => {
      this.showNotification = false;
      this.cdr.markForCheck();
    }, 5000);
  }

  cancelChanges() {
    this.cellStates.forEach((state, key) => {
      if (state === 'add') {
        this.cellStates.set(key, 'normal');
      } else if (state === 'pre-delete') {
        this.cellStates.set(key, 'added');
      }
    });

    this.showActionButtons = false;
    this.cdr.markForCheck();
  }

  trackByEmployee(index: number, employee: Employee): number {
    return employee.u_id;
  }

  trackByDistrict(index: number, district: District): number {
    return district.district_id;
  }

  trackByDay(index: number, day: number): number {
    return day;
  }

  getActiveCellsCount(districtId: number, weekDay: number): number {
    let count = 0;
    this.filteredEmployees.forEach((employee) => {
      const state = this.getCellState(employee.u_id, districtId, weekDay);
      if (state === 'added' || state === 'add') {
        count++;
      }
    });
    return count;
  }

  toggleEmployeePopup(employeeId: number, hasSchedule: boolean, event: Event) {
    event.stopPropagation();

    if (!hasSchedule) {
      return;
    }

    if (this.openedPopupEmployeeId === employeeId) {
      this.openedPopupEmployeeId = null;
    } else {
      this.openedPopupEmployeeId = employeeId;
    }
  }

  closeEmployeePopup() {
    this.openedPopupEmployeeId = null;
  }

  copySchedule(employeeId: number) {
    console.log('Копирование графика для сотрудника:', employeeId);
    this.closeEmployeePopup();
  }

  deleteEmployeeSchedule(employeeId: number) {
    console.log('Удаление графика для сотрудника:', employeeId);
    this.closeEmployeePopup();
  }

  onHeaderScroll(event: Event) {
    if (this.isBodyScrolling) return;
    this.isHeaderScrolling = true;

    const headerElement = event.target as HTMLElement;
    if (this.bodyScroll && this.bodyScroll.nativeElement) {
      this.bodyScroll.nativeElement.scrollLeft = headerElement.scrollLeft;
    }

    setTimeout(() => {
      this.isHeaderScrolling = false;
    }, 0);
  }

  onBodyScroll(event: Event) {
    if (this.isHeaderScrolling) return;
    this.isBodyScrolling = true;

    const bodyElement = event.target as HTMLElement;
    if (this.headerScroll && this.headerScroll.nativeElement) {
      this.headerScroll.nativeElement.scrollLeft = bodyElement.scrollLeft;
    }

    setTimeout(() => {
      this.isBodyScrolling = false;
    }, 0);
  }
}
